import React from "react"
import classNames from "classnames"
import GameControlButton, { ControlAccent } from "@/Components/GameControlButton"
import { MainButton } from "@/Components/MainButton"
import Money from "@/Components/Money"
import { images } from "@/utils/data"

/**
 * The content behind the in-game Help button (HelpModal).
 *
 * Everything a player can see or press during a game gets one entry here, with
 * a miniature of the real thing next to it. The miniatures are built from the
 * same components the game itself uses — GameControlButton, MainButton, the
 * real Font Awesome icons and the real colour values — rather than being drawn
 * again, so a new player can match what they read here against what is on
 * screen without translating between two visual languages. They are rendered
 * `pointer-events-none` so they read as pictures, not controls.
 *
 * Descriptions say what happens in *this* game, not the boxed one — the
 * auction rule, the steal-tax pile and the turn limit are all house rules a new
 * player has no reason to expect.
 */

export interface IHelpEntry {
  id: string
  name: string
  /** What it is and what it does, in a sentence or two. */
  description: string
  /** Where on screen to look for it. */
  where?: string
  /** The non-obvious bit — the thing players get wrong. */
  tip?: string
  /** Extra words the search box should match on. */
  keywords?: string
  preview: React.ReactNode
}

export interface IHelpCategory {
  id: string
  label: string
  icon: string
  /** One-liner under the label in the rail. */
  hint: string
  /** Sentence under the category title in the header. */
  blurb: string
  entries: IHelpEntry[]
}

// ---------------------------------------------------------------------------
// Preview primitives
// ---------------------------------------------------------------------------

/** A HUD "key" from the top-right cluster, at rest. */
const Ctrl: React.FC<{ icon: string; accent: ControlAccent; label?: string }> = ({
  icon,
  accent,
  label,
}) => (
  <GameControlButton
    accent={accent}
    tabIndex={-1}
    aria-hidden="true"
    className="pointer-events-none !px-3 !py-2"
  >
    <i className={classNames("fa-solid", icon)}></i>
    {label && <span className="font-poppins text-xs">{label}</span>}
  </GameControlButton>
)

/** One of the rounded action buttons (roll, build, trade…). */
const Action: React.FC<{
  color: string
  icon?: string
  img?: string
  label: string
  badge?: string
}> = ({ color, icon, img, label, badge }) => (
  <MainButton
    baseColor={color}
    size="sm"
    tabIndex={-1}
    aria-hidden="true"
    className="pointer-events-none !h-9 !min-w-0 !px-3"
  >
    <div className="flex flex-row items-center gap-2">
      {icon && <i className={classNames("fa-solid text-white text-xs", icon)}></i>}
      {img && <img className="h-4 w-auto object-contain" src={img} alt="" />}
      <span className="font-poppins text-[0.6875rem] font-semibold uppercase tracking-wide text-white/90">
        {label}
      </span>
      {badge && (
        <span className="rounded-full bg-black/25 px-1.5 py-0.5 font-poppins text-[0.625rem] font-semibold leading-none text-white">
          {badge}
        </span>
      )}
    </div>
  </MainButton>
)

/**
 * A board tile, at roughly the proportions of a real one: the board's beige, a
 * hairline dark edge, and an optional colour strip along the top for property
 * tiles.
 */
const Tile: React.FC<{
  strip?: string
  children?: React.ReactNode
  corner?: boolean
  className?: string
}> = ({ strip, children, corner, className }) => (
  <div
    className={classNames(
      "flex flex-col overflow-hidden border border-black/60 bg-[#f8e5cc] shadow-md",
      corner ? "h-[3.25rem] w-[3.25rem]" : "h-[3.5rem] w-[2.5rem]",
      className
    )}
  >
    {strip && (
      <div
        className="h-[0.7rem] shrink-0 border-b border-black/40"
        style={{ backgroundColor: strip }}
      />
    )}
    <div className="flex flex-1 items-center justify-center px-0.5 text-center leading-none text-[#3d2408]">
      {children}
    </div>
  </div>
)

/** A pill/badge from the board centre or the sidebars. */
const Pill: React.FC<{
  className?: string
  children: React.ReactNode
}> = ({ className, children }) => (
  <div
    className={classNames(
      "flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 font-poppins text-[0.625rem] font-bold shadow-lg ring-1",
      className
    )}
  >
    {children}
  </div>
)

/** The dark glass card every pop-up screen wears, shrunk to a thumbnail. */
const Screen: React.FC<{ icon: string; tint: string; caption: string }> = ({
  icon,
  tint,
  caption,
}) => (
  <div className="w-[4.75rem] overflow-hidden rounded-lg border border-gray-700/60 bg-black/80 shadow-lg">
    <div className="flex items-center gap-1 border-b border-white/10 px-1.5 py-1">
      <span className="h-1.5 w-1.5 rounded-full bg-rose-500/80" />
      <span className="h-1.5 w-1.5 rounded-full bg-white/20" />
    </div>
    <div className="flex flex-col items-center gap-1 px-1.5 py-2">
      <span
        className="flex h-6 w-6 items-center justify-center rounded-lg"
        style={{ backgroundColor: tint + "33", color: tint }}
      >
        <i className={classNames("fa-solid text-[0.6875rem]", icon)}></i>
      </span>
      <span className="text-center font-poppins text-[0.5rem] font-semibold uppercase leading-tight tracking-wide text-white/60">
        {caption}
      </span>
    </div>
  </div>
)

/** A round icon chip, for concepts with no single control of their own. */
const Chip: React.FC<{ icon?: string; tint: string; children?: React.ReactNode }> = ({
  icon,
  tint,
  children,
}) => (
  <span
    className="flex h-11 w-11 items-center justify-center rounded-full text-base ring-1"
    style={{ backgroundColor: tint + "26", color: tint, boxShadow: `0 0 14px ${tint}33` }}
  >
    {children ?? <i className={classNames("fa-solid", icon)}></i>}
  </span>
)

// ---------------------------------------------------------------------------
// The guide
// ---------------------------------------------------------------------------

export const helpCategories: IHelpCategory[] = [
  {
    id: "basics",
    label: "Start here",
    icon: "fa-flag-checkered",
    hint: "How a game is won",
    blurb:
      "The five things that decide every game. Read these once and the rest of the screen makes sense.",
    entries: [
      {
        id: "goal",
        name: "How you win",
        keywords: "win goal victory net worth bankrupt",
        description:
          "Two ways to win. Bankrupt every other player and the game ends there and then. Otherwise the game runs to its turn limit, and whoever has the highest net worth — cash plus properties plus houses — takes it.",
        where: "Turns left is counted by the badge in the top-right of the board's middle.",
        tip: "Because net worth decides it, cash sitting in your pocket counts too. Being asset-poor but liquid is a real strategy on a short game.",
        preview: <Chip icon="fa-trophy" tint="#f0b45c" />,
      },
      {
        id: "turn-loop",
        name: "What happens in a turn",
        keywords: "turn order roll move end turn doubles",
        description:
          "Roll the dice, your piece moves, the tile you land on does something, then you build, trade or play cards before pressing End Turn. Roll a double and you get another roll before you end.",
        where: "The turn buttons appear along the bottom of the board's middle — but only on your turn.",
        tip: "Trading is the exception: you can offer a deal at any moment, including on somebody else's turn.",
        preview: <Chip icon="fa-arrows-rotate" tint="#4f8fc9" />,
      },
      {
        id: "money",
        name: "Reading money",
        keywords: "money cash currency M price balance",
        description:
          "Every amount in the game is written with an M in front of it. That is the game's currency — prices, rent, bids and your balance all use it.",
        where: "Your own balance sits on your player card in the sidebar.",
        preview: (
          <Chip tint="#4ade80">
            <Money money={500} className="gap-0.5 text-xs font-semibold" iconClass="h-3" />
          </Chip>
        ),
      },
      {
        id: "auction-rule",
        name: "Property goes to auction",
        keywords: "auction buy property bidding hidden bid sealed",
        description:
          "This is the big house rule. Landing on an unowned property does not let you buy it — it puts the property up for auction, and everybody at the table bids at once, in secret. Highest bid wins it.",
        where: "The auction screen takes over the whole window when one starts.",
        tip: "Only two things skip the auction: the Private Buyer role's direct purchases, and a Forced Buy action card.",
        preview: <Chip icon="fa-gavel" tint="#2e8b70" />,
      },
      {
        id: "sets",
        name: "Sets, houses and rent",
        keywords: "set colour color group houses hotel rent monopoly build",
        description:
          "Own every property of one colour and you hold a set. Sets are the only properties you can build houses on, and houses are what turn small rent into game-ending rent.",
        where: "The colour strip along the top of a property tile tells you which set it belongs to.",
        tip: "One missing property blocks the whole set. That is what trades are for.",
        preview: (
          <div className="flex items-end gap-1">
            <Tile strip="#d93a3a">
              <img src={images.houseImage} className="h-4 w-auto object-contain" alt="" />
            </Tile>
            <Tile strip="#d93a3a">
              <img src={images.hotelImage} className="h-4 w-auto object-contain" alt="" />
            </Tile>
          </div>
        ),
      },
    ],
  },

  {
    id: "turn",
    label: "Your turn",
    icon: "fa-dice",
    hint: "The buttons you press",
    blurb:
      "These sit along the bottom of the board's middle. Most of them only appear while it is actually your turn.",
    entries: [
      {
        id: "roll",
        name: "Roll",
        keywords: "roll dice move",
        description:
          "Rolls the dice and moves your piece. Greys out while an animation is running, while an auction is open, and once you have already rolled — unless you rolled a double, which earns you another go.",
        where: "First of the turn buttons, bottom-middle of the board.",
        preview: <Action color="#d48e3d" icon="fa-dice" label="Roll" />,
      },
      {
        id: "buy-houses",
        name: "Buy houses",
        keywords: "build house hotel upgrade rent construction",
        description:
          "Puts the board into build mode: every property you can build on lights up, and clicking one adds a house and raises its rent. Press Done when you have finished.",
        where: "Second turn button. Greyed out until you own a complete colour set.",
        tip: "Houses are permanent value — they count towards the net worth that decides a turn-limited game.",
        preview: <Action color="#2f9e44" icon="fa-house" label="Buy houses" />,
      },
      {
        id: "sell-houses",
        name: "Sell houses",
        keywords: "sell house downgrade cash raise money emergency",
        description:
          "The same board mode in reverse: click a highlighted property to sell a house back and take the cash. Greyed out if you have no houses standing.",
        where: "Third turn button.",
        tip: "This is your emergency cash. If your balance goes below zero you have a few turns to climb back out before you are removed.",
        preview: <Action color="#b6a84b" icon="fa-house-circle-xmark" label="Sell houses" />,
      },
      {
        id: "cards",
        name: "Play cards / See your cards",
        keywords: "cards hand action card play deck",
        description:
          "Opens your hand of action cards. The number on the button is how many you are holding. On your turn you can play them; off-turn the button stays up so you can still read what you have.",
        where: "Fourth turn button — the only one that stays on screen when it is not your turn.",
        tip: "Cards are held, not spent on the spot. Landing on an action tile hands you one to keep for whenever it hurts most.",
        preview: <Action color="#8b5cf6" img={images.icons.cards} label="Cards" badge="3" />,
      },
      {
        id: "end-turn",
        name: "End Turn",
        keywords: "end turn pass finish next player",
        description:
          "Hands play to the next player. Stays disabled until you have done everything the tile you landed on requires.",
        where: "On its own row below the other turn buttons.",
        preview: <Action color="#c45a3d" icon="fa-rotate-right" label="End turn" />,
      },
      {
        id: "now-playing",
        name: "“… is playing”",
        keywords: "whose turn now playing active player waiting",
        description:
          "Whose turn it is, with their piece next to the name. It fills the same slot your turn buttons use, so the middle of the board always answers “who are we waiting for?”",
        where: "Bottom-middle of the board, whenever it is not your turn.",
        preview: <Chip icon="fa-user-clock" tint="#8a5a2b" />,
      },
      {
        id: "idle-badge",
        name: "Idle countdown",
        keywords: "idle afk auto roll auto end timeout away stalled",
        description:
          "Appears when whoever is up has gone quiet, and counts down to the move the game will make for them — rolling if they have not rolled, ending the turn if they have. Everyone sees it, so a still board reads as a distracted player rather than a crash.",
        where: "Just below the “… is playing” line.",
        tip: "Turns red in the last few seconds. Any input from that player cancels it.",
        preview: (
          <Pill className="bg-[#3d2408]/90 text-white/90 ring-[#f0b45c]/60">
            <i className="fa-solid fa-hourglass-half text-[#f0b45c]"></i>
            <span>4s</span>
          </Pill>
        ),
      },
      {
        id: "decision-wait",
        name: "Waiting on a decision",
        keywords: "waiting decision frozen pending banner paused",
        description:
          "Some cards and tiles hand one player a choice, and the game freezes for everyone else until they answer. This banner says who is deciding and what about, so the pause has a reason attached.",
        where: "Takes over the “… is playing” slot while a decision is open.",
        preview: <Chip icon="fa-hourglass-half" tint="#f0b45c" />,
      },
    ],
  },

  {
    id: "board",
    label: "Board tiles",
    icon: "fa-chess-board",
    hint: "What each square does",
    blurb:
      "What happens when your piece lands on each kind of square. Click any property tile, at any time, to read its full card.",
    entries: [
      {
        id: "go",
        // "Start" is what the corner itself says, and what the diagram calls it.
        name: "Start",
        keywords: "start go salary pass collect corner stock exchange invest",
        description:
          "The corner every lap starts from. Passing it pays you a salary, and opens the Stock Exchange — the one moment in a lap you can put cash into stock or take an older stake back out.",
        where: "Bottom-right corner of the board.",
        tip: "The Financer role collects extra every time they pass it.",
        preview: (
          <Tile corner>
            <span className="font-poppins text-[0.625rem] font-black tracking-wide">START</span>
          </Tile>
        ),
      },
      {
        id: "property",
        name: "Property tile",
        keywords: "property street colour color rent buy own land",
        description:
          "The tiles you are all fighting over. Land on an unowned one and it goes to auction; land on somebody else's and you pay them rent. Landing on your own does nothing.",
        where: "Along all four sides. The colour strip marks its set.",
        tip: "Clicking any property tile — on anyone's turn — opens its full card with the rent ladder on it.",
        preview: (
          <Tile strip="#4f8fc9">
            <span className="font-poppins text-[0.5rem] font-bold leading-tight">M140</span>
          </Tile>
        ),
      },
      {
        id: "action-tile",
        name: "Action card tile",
        keywords: "action card chance draw keep hand trickster",
        description:
          "Landing here deals you an action card, and you keep it in your hand to play whenever you like. Nothing happens the moment you draw it.",
        where: "Spread around all four sides of the board.",
        tip: "The Trickster role draws two cards here instead of one.",
        preview: (
          <Tile>
            <img
              src={images.actionCardBack}
              className="h-full w-auto object-contain py-0.5"
              alt=""
            />
          </Tile>
        ),
      },
      {
        id: "event-tile",
        name: "Event tile",
        keywords: "event community chest card immediate resolves",
        description:
          "Also deals a card, but this one resolves the instant it is drawn — money in, money out, a move, a penalty. Nothing to hold and nothing to decide.",
        where: "Mixed in with the action tiles around the board.",
        tip: "Action tile means “something to use later”. Event tile means “something happening right now”.",
        preview: (
          <Tile>
            <img
              src={images.eventCardBack}
              className="h-full w-auto object-contain py-0.5"
              alt=""
            />
          </Tile>
        ),
      },
      {
        id: "tax-tile",
        name: "Tax tile",
        keywords: "tax pay fine penalty bank charge",
        description:
          "Charges you a fixed amount. The money does not vanish into the bank — it goes onto the pile in the middle of the board, where it sits waiting for somebody.",
        where: "A couple of tiles per board.",
        preview: (
          <Tile>
            <i className="fa-solid fa-file-invoice-dollar text-[0.8rem]"></i>
          </Tile>
        ),
      },
      {
        id: "steal-tax-tile",
        name: "Steal Tax tile",
        keywords: "steal tax pile jackpot money on table free parking take",
        description:
          "The payoff for every tax anybody has paid. Land here and the entire pile in the middle of the board is yours, in one go, at no cost.",
        where: "One tile per board, on a side edge — the tile the money pile is sitting next to.",
        tip: "Watch the pile grow. Late in a game it can be worth more than a full colour set, and it swings games.",
        preview: (
          <Tile>
            <i className="fa-solid fa-mask text-[0.8rem] text-amber-600"></i>
          </Tile>
        ),
      },
      {
        id: "decision-hub-tile",
        name: "Decision Hub tile",
        keywords: "decision hub money or cards reward choice gift",
        description:
          "A free reward with a choice attached: take the cash, or take a handful of action cards instead. A timer runs while you decide, and the rest of the table waits on you.",
        where: "One or two tiles per board.",
        tip: "Cash is safe; cards are leverage. Take cards when you are behind and need something to swing with.",
        preview: (
          <Tile>
            <i className="fa-solid fa-gift text-[0.8rem] text-violet-600"></i>
          </Tile>
        ),
      },
      {
        id: "stock-tile",
        name: "Stock Exchange tile",
        keywords: "stock exchange invest interest wall street returns growth",
        description:
          "Lets you put money into stocks. The stake grows every round it stays invested, and you pull it back out later — bigger than it went in.",
        where: "One tile per board.",
        tip: "The Wall Street Wolf role doubles the growth rate on every stake.",
        preview: (
          <Tile>
            <img src={images.icons.stock} className="h-4 w-auto object-contain" alt="" />
          </Tile>
        ),
      },
      {
        id: "jail-tile",
        name: "Jail / Just visiting",
        keywords: "jail visiting prison corner handcuffs",
        description:
          "Land on it by moving normally and you are only visiting — nothing happens. Get sent here properly and your next turn opens with a choice of how to get out.",
        where: "Corner of the board.",
        tip: "Being jailed costs you the extra roll that doubles would normally earn you.",
        preview: (
          <Tile corner>
            <i className="fa-solid fa-handcuffs text-[0.8rem]"></i>
          </Tile>
        ),
      },
      {
        id: "go-to-jail",
        name: "Go To Jail",
        keywords: "go to jail sent arrested corner",
        description: "Sends your piece straight to the jail corner. Your turn ends where it stands.",
        where: "The opposite corner from jail.",
        preview: (
          <Tile corner>
            <i className="fa-solid fa-person-walking-arrow-right text-[0.8rem] text-red-700"></i>
          </Tile>
        ),
      },
      {
        id: "owner-token",
        name: "Owner marker",
        keywords: "owner token piece who owns marker taken",
        description:
          "The small piece shown on a property tile is whoever owns it. If you see one, that property is taken and landing on it costs you rent.",
        where: "On the property tile itself.",
        preview: <Chip icon="fa-chess-pawn" tint="#137dbd" />,
      },
      {
        id: "houses-on-tile",
        name: "Houses and hotels",
        keywords: "house hotel building rent upgrade",
        description:
          "Little buildings sitting on a property. Each one multiplies what that property charges. Four houses become a hotel, and a hotel is the most expensive square on the board to land on.",
        where: "Along the property's colour strip.",
        preview: (
          <div className="flex items-end gap-1">
            <img src={images.houseImage} className="h-6 w-auto object-contain" alt="" />
            <img src={images.hotelImage} className="h-7 w-auto object-contain" alt="" />
          </div>
        ),
      },
      {
        id: "mortgaged",
        name: "Mortgaged property",
        keywords: "mortgage mortgaged overlay no rent crossed out",
        description:
          "A property crossed out on the board has been mortgaged. It still belongs to its owner and still counts towards their set, but it charges no rent until it is bought back.",
        where: "Drawn over the tile itself.",
        preview: (
          <Tile strip="#8a5a2b" className="opacity-60">
            <i className="fa-solid fa-ban text-[0.8rem] text-red-700"></i>
          </Tile>
        ),
      },
    ],
  },

  {
    id: "center",
    label: "Middle of the board",
    icon: "fa-bullseye",
    hint: "Clocks, log and the pile",
    blurb:
      "The heads-up display inside the board's square: how much time is left, what just happened, and what is up for grabs.",
    entries: [
      {
        id: "turn-timer",
        name: "Turn timer",
        keywords: "timer clock time left countdown turn ring",
        description:
          "The ring counting down how long the current player has. It runs for whoever is up — including you — and freezes whenever the game is waiting on a decision or is paused.",
        where: "Top-right corner of the board's middle.",
        tip: "It ticks audibly on your own turn only, so you can look away from the screen without losing your turn.",
        preview: (
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-black/25 ring-1 ring-white/25">
            <span className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-amber-300/80 font-poppins text-[0.625rem] font-bold text-white">
              42
            </span>
          </span>
        ),
      },
      {
        id: "turns-left",
        name: "Turns left",
        keywords: "turns left limit rounds endgame end of game clock",
        description:
          "How many rounds the game has left. One turn here means a full round — every player rolling once — not your own turns. At zero the game ends and the highest net worth wins.",
        where: "Directly under the turn timer.",
        tip: "Hover or tap it for the full explanation. It turns amber near the end and red on the final round.",
        preview: (
          <Pill className="bg-gradient-slider-thumb text-[#3d2408] ring-black/10">
            <i className="fa-solid fa-hourglass-half opacity-80"></i>
            <span className="text-xs">12</span>
            <span className="uppercase tracking-wide">turns left</span>
          </Pill>
        ),
      },
      {
        id: "money-on-table",
        name: "Money on the table",
        keywords: "pile pot money table steal tax jackpot bills",
        description:
          "The growing pile of every tax and fine anyone has paid. It sits in the middle in plain sight and belongs to nobody — until somebody lands on the Steal Tax tile and takes all of it.",
        where: "Top of the board's middle. Only there once something is in it.",
        tip: "Hover it to see the exact amount waiting.",
        preview: <Chip icon="fa-money-bill-wave" tint="#f59e0b" />,
      },
      {
        id: "game-log",
        name: "Game log",
        keywords: "log history feed events what happened recent",
        description:
          "A running list of what just happened — rent paid, auctions won, houses built, cards played, stock moves and steals — with each player's name in their piece's colour.",
        where: "Left of centre inside the board.",
        tip: "This is the fastest way to work out why somebody's money suddenly moved.",
        preview: <Chip icon="fa-scroll" tint="#8a5a2b" />,
      },
      {
        id: "dice",
        name: "The dice",
        keywords: "dice roll 3d physics doubles",
        description:
          "Real tumbling dice that land on the roll the game actually made. On your turn they roll in a small tray; on everyone else's they roll across the whole middle.",
        where: "Middle of the board.",
        tip: "Two matching numbers is a double — you roll again before your turn ends.",
        preview: <Chip icon="fa-dice" tint="#e0282b" />,
      },
      {
        id: "card-deck",
        name: "The card deck",
        keywords: "deck cards chance draw pile animation stack",
        description:
          "The stack of face-down cards. When someone lands on an action or event tile, their card flies off this deck, so you can see who drew and when.",
        where: "Left side of the board's middle, inside the dashed outline.",
        preview: (
          <img
            src={images.actionCardBack}
            className="h-11 w-auto rounded object-contain shadow-lg"
            alt=""
          />
        ),
      },
      {
        id: "construction-ban",
        name: "Construction ban",
        keywords: "construction ban no building wrench blocked houses frozen",
        description:
          "A card can freeze all building for a few rounds. While this badge is up nobody can buy houses, and the number on it is how many turns are left until building reopens.",
        where: "Top-right corner of the board itself, outside the middle.",
        preview: (
          <Pill className="bg-red-500/90 text-white ring-white/20">
            <i className="fa-solid fa-wrench"></i>
            <span className="text-xs">2</span>
          </Pill>
        ),
      },
    ],
  },

  {
    id: "players",
    label: "Players & panels",
    icon: "fa-users",
    hint: "The two side columns",
    blurb:
      "The columns down either side of the board: who is playing, what they own, and everything you can start outside your own turn.",
    entries: [
      {
        id: "player-card",
        name: "Player card",
        keywords: "player card money properties avatar opponent sidebar",
        description:
          "One card per player, showing their piece, name, cash and a strip of every property they own. The card belonging to whoever is up is marked, so you can always see who you are waiting for.",
        where: "Stacked down both sidebars.",
        tip: "Read the property strips before you trade. A player one tile short of a set will pay well over the odds for it.",
        preview: <Chip icon="fa-id-card" tint="#e8b055" />,
      },
      {
        id: "role-badge",
        name: "Role badge",
        keywords: "role badge ability power perk rent lord trickster financer",
        description:
          "The small coloured tag on a player card naming the role they picked at the start. Roles are permanent advantages — cheaper building, extra rent, extra cards, auction discounts.",
        where: "On each player card.",
        tip: "Hover or tap any badge — including your opponents' — to read exactly what that role does. Knowing a rival is a Rent Lord changes which properties you let them have.",
        preview: (
          <span
            className="rounded-md px-2 py-0.5 font-poppins text-[0.625rem] font-medium"
            style={{ backgroundColor: "#F59E0B40", color: "#F59E0B" }}
          >
            Rent Lord
          </span>
        ),
      },
      {
        id: "bank",
        name: "Bank",
        keywords: "bank unsold properties available remaining left",
        description:
          "Every property nobody owns yet. It shrinks as the game goes on, and when it is empty the only way to get a property is to trade for it.",
        where: "Bottom of the left sidebar.",
        tip: "Check what is left in here before you overbid. If two tiles of a set are still in the bank, you have time.",
        preview: (
          <div className="flex h-11 w-16 items-center justify-center rounded bg-black/40 ring-1 ring-white/20">
            <span
              className="font-poppins text-[0.5rem] font-semibold tracking-[0.2em] text-white"
              style={{ writingMode: "vertical-lr" }}
            >
              BANK
            </span>
          </div>
        ),
      },
      {
        id: "create-trade",
        name: "Create Trade",
        keywords: "trade offer deal swap negotiate handshake",
        description:
          "Starts an offer to any player: pick who, then put properties and money on either side. They get a timed notification and accept or decline.",
        where: "Left sidebar, just above the bank.",
        tip: "Available all game, on anybody's turn — you never have to wait for your own turn to make a deal.",
        preview: <Action color="#4f8fc9" icon="fa-handshake" label="Create trade" />,
      },
      {
        id: "last-auction",
        name: "Last Auction Results",
        keywords: "last auction result who won bid history gavel",
        description:
          "Re-opens the results of the most recent auction — which property, who won it and for how much. The tile it went for is shown right on the button.",
        where: "Left sidebar above Create Trade. Only appears once an auction has finished.",
        tip: "Worth a look after every auction: what your rivals were willing to pay tells you how much cash they are sitting on.",
        preview: <Action color="#2e8b70" icon="fa-gavel" label="Last auction" />,
      },
      {
        id: "trades-panel",
        name: "Active trades",
        keywords: "trades list offers pending incoming panel",
        description:
          "Every offer currently in the air, plus the ones that just closed. A row waiting on you is marked with a dot, and each live offer runs its own clock.",
        where: "Bottom of the right sidebar.",
        preview: <Chip icon="fa-arrow-right-arrow-left" tint="#4f8fc9" />,
      },
      {
        id: "fast-pace",
        name: "Make game fast pace",
        keywords: "fast pace speed quick timers faster vote propose",
        description:
          "Asks the table to switch to fast pace: shorter timers and quicker animations. Pressing it opens a vote rather than changing anything on its own.",
        where: "Top of the left sidebar, above the first player card.",
        tip: "The switch is one-way — once the table agrees, the game stays fast for the rest of it.",
        preview: <Action color="#8b5cf6" icon="fa-bolt" label="Fast pace" />,
      },
      {
        id: "fast-pace-vote",
        name: "Fast pace vote",
        keywords: "vote fast pace yes no agree panel",
        description:
          "Once somebody proposes fast pace, this panel replaces the button and asks everyone to vote. It shows who has answered and how long is left to decide.",
        where: "Same slot at the top of the left sidebar.",
        preview: <Chip icon="fa-check-to-slot" tint="#8b5cf6" />,
      },
      {
        id: "fast-pace-badge",
        name: "Fast paced badge",
        keywords: "fast paced pace badge active enabled quick game",
        description:
          "Stays in that slot for the rest of the game once the table agrees to play fast paced, so a suddenly shorter turn timer has a visible reason rather than feeling like a bug.",
        where: "Top of the left sidebar.",
        preview: (
          <Pill className="border border-violet-400/60 bg-violet-600/80 text-violet-100 ring-transparent">
            <i className="fa-solid fa-bolt text-violet-200"></i>
            <span className="uppercase tracking-wide">Fast paced</span>
          </Pill>
        ),
      },
      {
        id: "challenge-badge",
        name: "Challenge",
        keywords: "challenge mission objective goal solo",
        description:
          "In a challenge game, this names the objective you are playing for. It stays on screen the whole game so the goal is never more than a glance away.",
        where: "Bottom-right of the screen, outside the board.",
        preview: <Chip icon="fa-bullseye" tint="#f0b45c" />,
      },
      {
        id: "bot-takeover",
        name: "Disconnect countdown",
        keywords: "disconnect bot takeover offline connection lost dropped",
        description:
          "A player who loses connection gets a countdown on their card. If they do not make it back in time a bot takes their seat, so the game keeps moving instead of stalling.",
        where: "On the disconnected player's card.",
        preview: <Chip icon="fa-plug-circle-xmark" tint="#f43f5e" />,
      },
    ],
  },

  {
    id: "screens",
    label: "Pop-up screens",
    icon: "fa-window-restore",
    hint: "What each one wants",
    blurb:
      "Screens the game opens on its own. Most are timed, and most freeze the rest of the table until you answer.",
    entries: [
      {
        id: "auction-screen",
        name: "Auction",
        keywords: "auction bid hidden sealed bidding win property",
        description:
          "Opens whenever an unowned property is landed on. Everyone bids at the same time and nobody sees anybody else's number until the clock runs out. Highest bid takes it; if nobody bids, the property stays with the bank.",
        tip: "Bids are sealed, so there is no bidding war to get carried away in — pick the number the property is actually worth to you.",
        preview: <Screen icon="fa-gavel" tint="#2e8b70" caption="Auction" />,
      },
      {
        id: "buy-or-auction",
        name: "Buy or auction",
        keywords: "buy or auction private buyer direct purchase choice",
        description:
          "The Private Buyer role's screen. It offers a property at its face price with no auction at all — or you can pass and let it go to auction like everything else.",
        tip: "The role only carries a few of these for the whole game, so spend them on a set-completing tile.",
        preview: <Screen icon="fa-tag" tint="#4f8fc9" caption="Buy" />,
      },
      {
        id: "your-cards",
        name: "Your cards",
        keywords: "cards hand play card modal action cards",
        description:
          "Your hand, laid out. On your turn each card can be played from here; off-turn you can still open it to read what you are holding.",
        preview: <Screen icon="fa-layer-group" tint="#8b5cf6" caption="Cards" />,
      },
      {
        id: "decision-hub-screen",
        name: "Choose your reward",
        keywords: "decision hub money cards reward choose timer",
        description:
          "The Decision Hub tile's screen: take the cash, or take cards. It is timed, and the whole table is waiting on your answer.",
        preview: <Screen icon="fa-gift" tint="#8b5cf6" caption="Reward" />,
      },
      {
        id: "stock-screen",
        name: "Stock Exchange",
        keywords: "stock invest money growth returns rounds slider",
        description:
          "Set how much to invest with a slider, and the screen projects what the stake will be worth after the next rounds before you commit.",
        tip: "Invested money is out of reach while it grows — do not put in cash you might need for rent.",
        preview: <Screen icon="fa-chart-line" tint="#34d399" caption="Stocks" />,
      },
      {
        id: "trade-screen",
        name: "Trade offer",
        keywords: "trade offer accept decline properties money deal",
        description:
          "Where a deal is built and answered: properties and cash on both sides, with the board itself clickable so you can add a property by tapping it. The other player gets a timer to accept or decline.",
        preview: <Screen icon="fa-handshake" tint="#4f8fc9" caption="Trade" />,
      },
      {
        id: "say-no-screen",
        name: "Say No",
        keywords: "say no cancel block counter card defend",
        description:
          "Pops up when something is about to happen to you and you are holding a card that can stop it. Play it to cancel the effect, or let the timer run out and take it.",
        preview: <Screen icon="fa-hand" tint="#f43f5e" caption="Say No" />,
      },
      {
        id: "double-rent-screen",
        name: "Double rent",
        keywords: "double rent card owner charge extra",
        description:
          "Offered to a property's owner when somebody lands on it and the owner holds a double-rent card. Play it and the rent that lands is doubled.",
        tip: "This one can interrupt your own turn — you may be asked while somebody else is rolling.",
        preview: <Screen icon="fa-arrow-up-right-dots" tint="#f0b45c" caption="Rent" />,
      },
      {
        id: "jail-screen",
        name: "Getting out of jail",
        keywords: "jail pay 50 roll doubles get out free card",
        description:
          "Opens at the start of your turn when you are jailed, with the ways out: pay a fine, roll for doubles, or spend a Get Out of Jail Free card if you have one.",
        preview: <Screen icon="fa-handcuffs" tint="#94a3b8" caption="Jail" />,
      },
      {
        id: "bankrupt-screen",
        name: "In debt",
        keywords: "bankrupt debt negative money broke lose eliminated",
        description:
          "Appears when your balance drops below zero. It gives you a few of your own turns to get back above nothing — by selling houses or trading — before you are removed from the game.",
        tip: "Its buttons jump straight to selling houses and to trading, which are the two ways out.",
        preview: <Screen icon="fa-triangle-exclamation" tint="#f43f5e" caption="Debt" />,
      },
      {
        id: "end-stats",
        name: "Final standings",
        keywords: "end game stats standings winner results final scores",
        description:
          "The end-of-game screen, with the winner and everyone's final numbers. Closing it is not final — a button appears in the middle of the board to open it again.",
        preview: <Screen icon="fa-ranking-star" tint="#f0b45c" caption="Results" />,
      },
    ],
  },

  {
    id: "controls",
    label: "Top-right controls",
    icon: "fa-sliders",
    hint: "Sound, screen, exit",
    blurb: "The row of keys in the top-right corner. None of them affect the game, except the last two.",
    entries: [
      {
        id: "help",
        name: "Help",
        keywords: "help guide question mark manual how to play",
        description:
          "This guide. Open it any time — the game carries on behind it, and nothing you do in here changes anything.",
        where: "The blue question mark in the top-right cluster.",
        tip: "Esc closes it.",
        preview: <Ctrl icon="fa-circle-question" accent="sky" />,
      },
      {
        id: "fullscreen",
        name: "Full screen",
        keywords: "fullscreen expand maximise maximize screen size",
        description:
          "Fills the screen with the game and back again. The board is sized off the window height, so full screen genuinely makes everything bigger.",
        where: "First key in the cluster.",
        preview: <Ctrl icon="fa-expand" accent="emerald" />,
      },
      {
        id: "music",
        name: "Music",
        keywords: "music sound audio mute toggle volume",
        description:
          "Mutes and unmutes the background music. A line through the note means it is off. Game sounds are set separately, under Settings.",
        where: "Second key in the cluster.",
        preview: <Ctrl icon="fa-music" accent="cyan" />,
      },
      {
        id: "settings",
        name: "Settings",
        keywords: "settings options background piece sound volume gear preferences",
        description:
          "Three things: the background behind the board, the piece you play as, and your sound and music volumes. Background and volumes apply immediately; your piece is saved to your account and used in every game.",
        where: "The purple gear.",
        preview: <Ctrl icon="fa-gear" accent="violet" />,
      },
      {
        id: "pause",
        name: "Pause",
        keywords: "pause resume stop freeze break",
        description:
          "Freezes the turn timer and puts an overlay over the board. Only offered when you are the only human at the table — you cannot make other players wait.",
        where: "The amber key, when it is there at all.",
        preview: <Ctrl icon="fa-pause" accent="amber" />,
      },
      {
        id: "leave",
        name: "Leave",
        keywords: "leave quit exit forfeit lobby",
        description: "Leaves the game and takes you back to the lobby. You are asked to confirm first.",
        where: "The red key on the end.",
        tip: "Leaving a game in progress counts as going bankrupt — your properties go back into play.",
        preview: <Ctrl icon="fa-sign-out-alt" accent="rose" label="Leave" />,
      },
    ],
  },
]

/** Flat list of every entry, tagged with the category it came from — for search. */
export const allHelpEntries: (IHelpEntry & {
  categoryId: string
  categoryLabel: string
})[] = helpCategories.flatMap((category) =>
  category.entries.map((entry) => ({
    ...entry,
    categoryId: category.id,
    categoryLabel: category.label,
  }))
)

export const helpEntryCount = allHelpEntries.length
